import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { HeartHandshake, Check, History, RefreshCw } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";
import { GriefResult, GriefTrendChart } from "../components/GriefResult";

const GriefQuestionnairePage = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [months, setMonths] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("intro");

  const load = () => {
    axios.get(`${API}/grief/items`, { withCredentials: true, params: { lang: locale } }).then((r) => setData(r.data)).catch(() => toast.error("Échelle indisponible"));
    axios.get(`${API}/grief/status`, { withCredentials: true }).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/grief/history`, { withCredentials: true }).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(load, [locale]);

  const items = data?.items || [];
  const answered = Object.keys(answers).length;
  const pending = status?.pending?.[0];
  const canSubmit = items.length > 0 && answered === items.length;
  const anchors = data?.anchors || [];

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/grief/submit`, { responses: answers, note, months_since_loss: months === "" ? null : Number(months), request_id: pending?.request_id }, { withCredentials: true });
      setResult(r.data); setMode("result"); setAnswers({}); setNote("");
      toast.success(t("griefScale.saved", "Échelle enregistrée"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSubmitting(false); }
  };

  const rows = useMemo(() => items.map((it) => (
    <div key={it.id} className={`rounded-lg border p-3 sm:p-4 ${answers[it.id] ? "bg-background" : "bg-muted/30"}`} data-testid={`grief-item-${it.id}`}>
      <p className="text-sm font-medium mb-2"><span className="text-muted-foreground mr-1">{it.id}.</span>{it.text}</p>
      <div className="grid grid-cols-5 gap-1">
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} type="button" onClick={() => setAnswers((a) => ({ ...a, [it.id]: v }))} title={anchors[v - 1]}
            className={`h-9 rounded-md text-xs sm:text-sm border transition-colors ${answers[it.id] === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`} data-testid={`grief-item-${it.id}-${v}`}>{anchors[v - 1] || v}</button>
        ))}
      </div>
    </div>
  )), [items, answers, anchors]);

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6" data-testid="grief-questionnaire-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2"><HeartHandshake className="w-7 h-7 text-primary" /> {data?.title || "Échelle de deuil"}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("griefScale.subtitle", "13 questions · 3 minutes · suivi de votre évolution dans le temps")}</p>
          </div>
          {status?.count > 0 && mode !== "history" && <Button variant="outline" size="sm" onClick={() => setMode("history")} className="gap-1.5" data-testid="grief-history-btn"><History className="w-4 h-4" /> {t("griefScale.history", "Mon évolution")} ({status.count})</Button>}
        </div>

        {pending && mode === "intro" && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm" data-testid="grief-pending-banner">
            {t("griefScale.pending", "Votre thérapeute vous invite à remplir cette échelle")}{pending.label ? ` — ${pending.label}` : ""}.
          </div>
        )}

        {mode === "intro" && (
          <Card>
            <CardContent className="p-5 space-y-4">
              <p className="text-sm leading-relaxed">{data?.intro}</p>
              <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
                <li>{t("griefScale.dim1", "Détresse de séparation : manque, préoccupation, solitude.")}</li>
                <li>{t("griefScale.dim2", "Détresse émotionnelle et évitement : acceptation, colère, culpabilité, évitement, perte de sens.")}</li>
                <li>{t("griefScale.dim3", "Retentissement sur la vie : activités et relations.")}</li>
              </ul>
              <Button onClick={() => setMode("fill")} data-testid="grief-start-btn" className="gap-2"><Check className="w-4 h-4" /> {status?.count > 0 ? t("griefScale.startAgain", "Refaire l'échelle") : t("griefScale.start", "Commencer")}</Button>
            </CardContent>
          </Card>
        )}

        {mode === "fill" && (
          <div className="space-y-3">
            <div className="sticky top-14 sm:top-0 z-10 bg-background/95 backdrop-blur py-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span>{answered}/{items.length}</span></div>
              <Progress value={items.length ? (answered / items.length) * 100 : 0} className="h-2" />
            </div>
            <div className="rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm flex-1" htmlFor="grief-months">{t("griefScale.monthsLabel", "Depuis combien de mois la perte a-t-elle eu lieu ? (facultatif)")}</label>
              <Input id="grief-months" type="number" min={0} max={1200} value={months} onChange={(e) => setMonths(e.target.value)} className="h-9 w-28 text-sm" data-testid="grief-months" />
            </div>
            {rows}
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("griefScale.notePlaceholder", "Note facultative (contexte du moment : anniversaire, fêtes, démarches…)")} className="text-sm" data-testid="grief-note" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMode("intro")}>{t("common.cancel", "Annuler")}</Button>
              <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="grief-submit-btn">{submitting ? "…" : t("griefScale.submit", "Voir mes résultats")}</Button>
            </div>
          </div>
        )}

        {mode === "result" && result && (
          <div className="space-y-4">
            <GriefResult scoring={result.scoring} subscales={data?.subscales} levels={data?.levels} />
            {history.length >= 2 && <GriefTrendChart entries={history} subscales={data?.subscales} />}
            <div className="flex gap-2"><Button variant="outline" onClick={() => setMode("intro")} className="gap-1.5"><RefreshCw className="w-4 h-4" /> {t("common.back", "Retour")}</Button></div>
          </div>
        )}

        {mode === "history" && (
          <div className="space-y-4" data-testid="grief-history">
            {history.length >= 2 ? <GriefTrendChart entries={history} subscales={data?.subscales} /> : <p className="text-sm text-muted-foreground">{t("griefScale.needTwo", "Remplissez l'échelle au moins deux fois pour voir votre évolution.")}</p>}
            {history.length > 0 && <GriefResult scoring={history[history.length - 1].scoring} subscales={data?.subscales} levels={data?.levels} title={t("griefScale.lastResult", "Dernier résultat")} />}
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">{t("griefScale.allResults", "Toutes les passations")}</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {[...history].reverse().map((e) => (
                  <div key={e.entry_id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50" data-testid={`grief-entry-${e.entry_id}`}>
                    <span className="text-muted-foreground">{new Date(e.created_at).toLocaleDateString(locale === "en" ? "en-GB" : locale, { day: "numeric", month: "short", year: "numeric" })}{e.label ? ` · ${e.label}` : ""}</span>
                    <span className="text-xs font-medium">{e.scoring.total} / {e.scoring.max_total} · {e.scoring.level}</span>
                  </div>
                ))}
              </CardContent></Card>
            <Button variant="outline" onClick={() => setMode("intro")}>{t("common.back", "Retour")}</Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GriefQuestionnairePage;
